/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import Component from '@glimmer/component';
import {
  HdsCardContainer,
  HdsTextBody,
  HdsBadge,
} from '@hashicorp/design-system-components/components';
import TitleRow from './base/title-row.gts';
import { REPLICATION_ENABLED_STATE } from '../../types/index.ts';
import type { IconName } from '@hashicorp/flight-icons/svg';
import { htmlSafe } from '@ember/template';

import './cluster-replication.scss';

export interface ClusterReplicationSignature {
  Args: {
    disasterRecoveryState: REPLICATION_ENABLED_STATE | 'disabled';
    performanceState: REPLICATION_ENABLED_STATE | 'disabled';
    isVaultDedicated: boolean;
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}

export default class ClusterReplication extends Component<ClusterReplicationSignature> {
  getState = (state: REPLICATION_ENABLED_STATE | 'disabled' = 'disabled') => {
    return state;
  };

  get isEmpty() {
    return (
      this.getState(this.args.disasterRecoveryState) === 'disabled' &&
      this.getState(this.args.performanceState) === 'disabled'
    );
  }

  get description() {
    if (this.isEmpty) {
      return htmlSafe(
        'Enable <a class="hds-link-inline--color-secondary" href="https://developer.hashicorp.com/vault/docs/internals/replication" target="_blank" data-test-vault-reporting-cluster-replication-description-link>replication</a> to replicate data across clusters.',
      );
    } else {
      return 'Status of disaster recovery and performance replication.';
    }
  }

  getIcon = (state: REPLICATION_ENABLED_STATE | 'disabled' = 'disabled') => {
    const iconMap: Record<string, IconName> = {
      disabled: 'x',
      primary: 'check',
      secondary: 'check',
      bootstrapping: 'loading',
    };
    return iconMap[state] || iconMap['disabled'];
  };

  getColor = (state: REPLICATION_ENABLED_STATE | 'disabled' = 'disabled') => {
    const colorMap: Record<string, HdsBadge['color']> = {
      disabled: 'neutral',
      primary: 'success',
      secondary: 'success',
      bootstrapping: 'neutral',
    };
    return colorMap[state] || colorMap['disabled'];
  };

  get linkUrl() {
    const { isVaultDedicated = false } = this.args;
    if (isVaultDedicated) {
      return;
    }
    return 'replication';
  }

  <template>
    <HdsCardContainer
      data-test-vault-reporting-cluster-replication
      @hasBorder={{true}}
      class="ssu-cluster-replication"
      ...attributes
    >
      <TitleRow
        @title="Cluster replication"
        @description={{this.description}}
        @linkUrl={{this.linkUrl}}
      />

      <HdsTextBody
        @size="300"
        class="ssu-cluster-replication__list-row__title"
        data-test-vault-reporting-cluster-replication-dr-row
      >
        Disaster Recovery
        <HdsBadge
          class="ssu-cluster-replication__list-row__badge"
          data-test-vault-reporting-cluster-replication-dr-badge
          @icon={{this.getIcon @disasterRecoveryState}}
          @text={{this.getState @disasterRecoveryState}}
          @color={{this.getColor @disasterRecoveryState}}
          @type="outlined"
          @size="small"
        />
      </HdsTextBody>

      <HdsTextBody
        @size="300"
        class="ssu-cluster-replication__list-row__title"
        data-test-vault-reporting-cluster-replication-perf-row
      >
        Performance
        <HdsBadge
          class="ssu-cluster-replication__list-row__badge"
          data-test-vault-reporting-cluster-replication-perf-badge
          @icon={{this.getIcon @performanceState}}
          @text={{this.getState @performanceState}}
          @color={{this.getColor @performanceState}}
          @type="outlined"
          @size="small"
        />
      </HdsTextBody>

    </HdsCardContainer>
  </template>
}
